import { z } from "zod";

export const alternarFavoritoSchema = z.object({
  tipo: z.enum(["procedimento", "protese"]),
  id: z.string().min(1),
});
export type AlternarFavoritoInput = z.infer<typeof alternarFavoritoSchema>;

export type GrupoDeChips<T> = { chave: "favoritos" | "profissional" | "todos"; itens: T[] };

/**
 * Ordem dos procedimentos no agendamento: favoritos da pessoa, depois os atribuídos pelo dono ao
 * profissional (sem repetir), e o resto fica atrás do "+ N outros". Sem nenhum dos dois, mostra os
 * primeiros `maxVisivel`. Um escolhido nunca cai no escondido — senão não dá pra desmarcar.
 */
export function agruparChips<T extends { id: string }>(
  servicos: T[],
  opcoes: { selecionados: string[]; atribuidos?: string[]; favoritos?: Iterable<string>; maxVisivel: number },
): { grupos: GrupoDeChips<T>[]; restantes: T[] } {
  const fav = new Set(opcoes.favoritos ?? []);
  const atribuidos = new Set(opcoes.atribuidos ?? []);
  const favoritos = servicos.filter((s) => fav.has(s.id));
  const doProfissional = servicos.filter((s) => !fav.has(s.id) && atribuidos.has(s.id));
  const demais = servicos.filter((s) => !fav.has(s.id) && !atribuidos.has(s.id));

  if (favoritos.length === 0 && doProfissional.length === 0) {
    const primeiros = demais.slice(0, opcoes.maxVisivel);
    const escolhidosFora = demais.slice(opcoes.maxVisivel).filter((s) => opcoes.selecionados.includes(s.id));
    const visiveis = [...primeiros, ...escolhidosFora];
    return {
      grupos: [{ chave: "todos", itens: visiveis }],
      restantes: demais.filter((s) => !visiveis.includes(s)),
    };
  }

  const escolhidosFora = demais.filter((s) => opcoes.selecionados.includes(s.id));
  const grupos: GrupoDeChips<T>[] = [];
  if (favoritos.length) grupos.push({ chave: "favoritos", itens: favoritos });
  if (doProfissional.length) grupos.push({ chave: "profissional", itens: doProfissional });
  grupos[grupos.length - 1].itens = [...grupos[grupos.length - 1].itens, ...escolhidosFora];
  return { grupos, restantes: demais.filter((s) => !escolhidosFora.includes(s)) };
}

/** Favoritos primeiro, mantendo a ordem de cada parte — usado na busca do agendamento. */
export function favoritosPrimeiro<T extends { id: string }>(itens: T[], favoritos: Set<string>): T[] {
  return [...itens.filter((i) => favoritos.has(i.id)), ...itens.filter((i) => !favoritos.has(i.id))];
}
